import { useCallback, useMemo } from "react";

import { Dashboard } from "api/model/dashboard/Dashboard";
import { DropdownChangeEvent } from "primereact/dropdown";
import { SelectItem } from "primereact/selectitem";
import { I, T } from "services/lang/LanguageService";
import { ArrowDirection } from "utils/enums";

export const UNGROUPED = "ungrouped";

export const useTabGroups = (
  dashboard: Dashboard,
  tabId: number,
  handleTabChange: (tabUUID: string, e?: { originalEvent?: Event | React.SyntheticEvent }) => void
) => {
  const selectedTabGroupUUID = useMemo(() => {
    return dashboard?.dashboardTabsJs?.[tabId]?.tabGroupId ?? UNGROUPED;
  }, [dashboard?.dashboardTabsJs, tabId]);

  const hasUngroupedTabs = useMemo(() => {
    return dashboard?.dashboardTabsJs?.some(tab => !tab.tabGroupId);
  }, [dashboard?.dashboardTabsJs]);

  const tabGroups = useMemo(() => {
    const groupIds = new Set(dashboard.dashboardTabsJs.map(tab => tab.tabGroupId));
    const tabGroupList = [...(dashboard.tabGroups ?? [])].reduce(
      (acc, group) =>
        groupIds.has(group.uuid)
          ? [
              ...acc,
              {
                value: group.uuid,
                label: I(group.title),
              },
            ]
          : acc,
      [] as SelectItem[]
    );

    return hasUngroupedTabs
      ? [{ value: UNGROUPED, label: T("label.unassigned") }, ...tabGroupList]
      : tabGroupList;
  }, [dashboard.dashboardTabsJs, dashboard.tabGroups, hasUngroupedTabs]);

  const tabsInGroup = useMemo(() => {
    return [...(dashboard.dashboardTabsJs ?? [])].reduce((acc, tab) => {
      if (selectedTabGroupUUID === UNGROUPED) {
        return !tab.tabGroupId ? [...acc, { value: tab.uuid, label: I(tab.title) }] : acc;
      }
      return tab.tabGroupId === selectedTabGroupUUID
        ? [...acc, { value: tab.uuid, label: I(tab.title) }]
        : acc;
    }, [] as SelectItem[]);
  }, [dashboard.dashboardTabsJs, selectedTabGroupUUID]);

  const handleTabGroupChange = useCallback(
    (e: DropdownChangeEvent) => {
      const selectedValue = e.value as string;
      const tabs = dashboard.dashboardTabsJs.filter(tab => {
        if (selectedValue === UNGROUPED) return !tab.tabGroupId;
        return tab.tabGroupId === selectedValue || tab.uuid === selectedValue;
      });

      if (tabs.length === 0) return;
      handleTabChange(tabs[0].uuid, e);
    },
    [dashboard.dashboardTabsJs, handleTabChange]
  );

  const changeTabWithArrow = useCallback(
    (direction: ArrowDirection, items: SelectItem[], selectedId: string, isGroup = false) => {
      const currentIndex = items.findIndex(node => node.value === selectedId);
      if (currentIndex === -1) return;

      const newIndex = direction === ArrowDirection.DOWN ? currentIndex + 1 : currentIndex - 1;
      if (newIndex < 0 || newIndex >= items.length) return;

      const tabUUID = dashboard.dashboardTabsJs.find(tab => {
        if (items[newIndex].value === UNGROUPED) return !tab.tabGroupId;
        return isGroup ? tab.tabGroupId === items[newIndex].value : tab.uuid === items[newIndex].value;
      })?.uuid;

      if (!tabUUID) return;
      handleTabChange(tabUUID);
    },
    [dashboard.dashboardTabsJs, handleTabChange]
  );

  return {
    selectedTabGroupUUID,
    tabGroups,
    tabsInGroup,
    handleTabGroupChange,
    changeTabWithArrow,
  };
};
